<template>
    <legal-page title="E-Mail Preferences" :effective="false">
        <div v-if="error" class="notification is-light is-warning">
            <i class="far fa-triangle-exclamation mr-2"></i>{{ error }}
            You can also manage notifications from <strong>Your Account &gt; Notifications</strong> after you log in.
        </div>

        <template v-else-if="prefs">
            <p v-if="prefs.first_name">Hi {{ prefs.first_name }},</p>

            <div class="unsub-box" v-if="!done">
                <p class="mb-4">Stop receiving <strong>{{ prefs.topic.label }}</strong> e-mails?</p>
                <b-button type="is-primary" :loading="saving" @click="unsubscribe">Unsubscribe</b-button>
            </div>
            <div class="unsub-box is-done" v-else>
                <p><i class="fas fa-check-circle has-text-success mr-2"></i>You're unsubscribed from <strong>{{ prefs.topic.label }}</strong> e-mails.</p>
            </div>

            <h3>Other E-Mail</h3>
            <p class="is-size-7 has-text-grey">
                Account messages, such as login codes, password resets, and enrollment confirmations, are always sent.
                To manage text messages, log in and go to Your Account &gt; Notifications.
            </p>

            <div class="pref-row" v-for="topic in optionalTopics" :key="topic.key">
                <div class="pref-topic">
                    <div class="has-text-weight-bold">{{ topic.label }}</div>
                    <div class="is-size-7 has-text-grey">{{ topic.description }}</div>
                </div>
                <b-switch :value="prefs.email_all && topic.email" type="is-success" size="is-small"
                          :disabled="saving || !prefs.email_all" @input="save([{topic: topic.key, channel: 'email', enabled: $event}])"/>
            </div>

            <div class="pref-row">
                <div class="pref-topic">
                    <div class="has-text-weight-bold">All Optional E-Mail</div>
                    <div class="is-size-7 has-text-grey">Turn off to stop every e-mail above at once.</div>
                </div>
                <b-switch :value="prefs.email_all" type="is-success" size="is-small" :disabled="saving"
                          @input="save([{topic: 'all', channel: 'email', enabled: $event}])"/>
            </div>
        </template>

        <div v-else class="has-text-grey"><i class="far fa-spinner fa-spin mr-2"></i>Loading your preferences…</div>
    </legal-page>
</template>

<script>
    import LegalPage from '@/components/legal/LegalPage';
    import {client as http} from '@/http_client';

    // Opened from an e-mail's unsubscribe link (/#/unsubscribe/:token); no login.
    // Opening the page changes nothing — link scanners prefetch URLs — the
    // Unsubscribe button does. Mail clients' one-click unsubscribe posts to the
    // API directly and never loads this page.
    export default {
        name: 'Unsubscribe',
        components: {LegalPage},
        props: {
            token: {type: String, required: true},
        },
        data() {
            return {prefs: null, error: null, saving: false, done: false};
        },
        computed: {
            optionalTopics() {
                return (this.prefs?.topics || []).filter(t => !t.required);
            },
        },
        methods: {
            url() {
                return `public/unsubscribe/${encodeURIComponent(this.token)}`;
            },
            load() {
                http.get(this.url()).then(res => {
                    this.prefs = res.data;
                }).catch(err => {
                    this.error = (err.response && err.response.data && err.response.data.error) || 'We could not load your preferences.';
                });
            },
            unsubscribe() {
                this.saving = true;
                const changes = [{topic: this.prefs.topic.key, channel: 'email', enabled: false}];
                http.post(this.url(), {changes}).then(res => {
                    this.prefs = res.data;
                    this.done = true;
                }).catch(() => {
                    this.$notify('error', 'Could Not Save', 'Something went wrong. Please try again.', {duration: 8000, permanent: false});
                }).finally(() => {
                    this.saving = false;
                });
            },
            save(changes) {
                this.saving = true;
                http.post(this.url(), {changes}).then(res => {
                    this.prefs = res.data;
                }).catch(() => {
                    this.$notify('error', 'Could Not Save', 'Something went wrong. Please try again.', {duration: 8000, permanent: false});
                    this.load();
                }).finally(() => {
                    this.saving = false;
                });
            },
        },
        mounted() {
            this.load();
        },
    };
</script>

<style lang="scss" scoped>
    .unsub-box {
        border: 1px solid #e7e7e7;
        border-radius: 8px;
        padding: 1.5rem;
        margin: 1.5rem 0 1rem;
        text-align: center;

        &.is-done {
            background: #f6fbf7;
        }
    }

    .pref-row {
        display: flex;
        align-items: center;
        padding: 0.85rem 0;
        border-bottom: 1px solid #f0f0f0;

        .pref-topic {
            flex: 1;
            padding-right: 1rem;
        }
    }
</style>
